import { useEffect, useState } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "../api.js";

// S-051 the part of S05 Subscription and credits that ST-067 needs (spec 4.1 S05, BILL-06,
// BILL-09): the Lite subscription and its period, the credit balance, and the payment history with
// every amount excluding and including VAT. Price group is never shown (S05). The rest of S05
// (ledger, Request credits, Request invoice, open requests) is S-056.
// S-053 BILL-07: the payment banner while Lite is past_due (with the date suspension happens) and
// what still works while it is suspended. Screens are paused (4 Nov 2026): kept minimal, it moves
// onto the ZD shell and S02 later.

type InvoiceStatus = "issued" | "paid" | "cancelled";
type SubscriptionStatus = "trial" | "active" | "past_due" | "suspended" | "cancelled";

export interface BillingAnswer {
  vatRateBasisPoints: number | null;
  subscription: { status: SubscriptionStatus; periodStart: string | null; periodEnd: string | null } | null;
  liteBanner?: { kind: "payment_due"; graceUntil: string | null } | { kind: "suspended" } | null;
  creditBalance: number;
  invoices: Array<{
    id: string;
    module: "lite" | "remembrance";
    number: string;
    amountExclVatCents: number;
    vatCents: number;
    amountInclVatCents: number;
    dueDate: string;
    creditsQuantity: number | null;
    periodStart: string | null;
    periodEnd: string | null;
    status: InvoiceStatus;
    paymentDate: string | null;
  }>;
}

const SUB_KEY = {
  trial: "parlour.subscription.status_trial",
  active: "parlour.subscription.status_active",
  past_due: "parlour.subscription.status_past_due",
  suspended: "parlour.subscription.status_suspended",
  cancelled: "parlour.subscription.status_cancelled",
} as const;
const INV_KEY = {
  issued: "parlour.subscription.invoice_issued",
  paid: "parlour.subscription.invoice_paid",
  cancelled: "parlour.subscription.invoice_cancelled",
} as const;

export const formatRand = (cents: number) =>
  `R${new Intl.NumberFormat("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100)}`;

export function Subscription() {
  const [data, setData] = useState<BillingAnswer | undefined>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    void api<BillingAnswer>("/v1/parlour/billing").then((r) => {
      if (r.ok) setData(r.data);
      else setError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("parlour.subscription.load_failed")));
    });
  }, []);

  return (
    <section aria-labelledby="subscription-title">
      <h2 id="subscription-title">{t("parlour.subscription.title")}</h2>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {!data && !error && <p aria-busy="true">{t("parlour.subscription.loading")}</p>}
      {data && (
        <>
          {data.liteBanner?.kind === "payment_due" && (
            <p role="status" data-testid="lite-banner">
              {t("billing.lite.banner_past_due")} {data.liteBanner.graceUntil}
            </p>
          )}
          {data.liteBanner?.kind === "suspended" && (
            <p role="status" data-testid="lite-banner">
              {t("billing.lite.banner_suspended")}
            </p>
          )}
          <h3>{t("parlour.subscription.lite")}</h3>
          {data.subscription ? (
            <p data-testid="lite-subscription">
              <strong>{t(SUB_KEY[data.subscription.status])}</strong>{" "}
              {data.subscription.periodEnd && (
                <>
                  {t("parlour.subscription.period")} {data.subscription.periodStart} {t("parlour.subscription.period_to")} {data.subscription.periodEnd}
                </>
              )}
            </p>
          ) : (
            <p>{t("parlour.subscription.no_lite")}</p>
          )}
          <p data-testid="credit-balance">
            {t("parlour.subscription.credits")} <strong>{data.creditBalance}</strong>
          </p>
          <h3>{t("parlour.subscription.history")}</h3>
          {data.invoices.length === 0 ? (
            <p>{t("parlour.subscription.none")}</p>
          ) : (
            <table aria-label={t("parlour.subscription.history")}>
              <thead>
                <tr>
                  <th scope="col">{t("parlour.subscription.col_number")}</th>
                  <th scope="col">{t("parlour.subscription.col_for")}</th>
                  <th scope="col">{t("parlour.subscription.col_due")}</th>
                  <th scope="col">{t("parlour.subscription.col_excl_vat")}</th>
                  <th scope="col">{t("parlour.subscription.col_vat")}</th>
                  <th scope="col">{t("parlour.subscription.col_incl_vat")}</th>
                  <th scope="col">{t("parlour.subscription.col_status")}</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id}>
                    <td>{i.number}</td>
                    <td>
                      {i.module === "lite" ? (
                        <>
                          {t("parlour.subscription.lite_period")} {i.periodStart} {t("parlour.subscription.period_to")} {i.periodEnd}
                        </>
                      ) : (
                        <>
                          {i.creditsQuantity} {t("parlour.subscription.credit_count")}
                        </>
                      )}
                    </td>
                    <td>{i.dueDate}</td>
                    <td>{formatRand(i.amountExclVatCents)}</td>
                    <td>{formatRand(i.vatCents)}</td>
                    <td>{formatRand(i.amountInclVatCents)}</td>
                    <td>
                      {t(INV_KEY[i.status])} {i.paymentDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
